import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import Button from '@/components/Button/Button'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import FormField from '@/components/FormField'
import Select from '@/components/Select/Select'
import Tooltip from '@/components/Tooltip/Tooltip'
import type { FormFieldType } from '@/types/form-field-type'

import styles from './BasketCourseArraySection.module.scss'
import BasketCourseSelect from './BasketCourseSelect'
import BasketCourseSemesterArraySection from './BasketCourseSemesterArraySection'
import {
  BASKET_CONFIG_BY_TYPE,
  EMPTY_COURSE,
  EMPTY_SEMESTERS
} from '../constants/curriculum-basket-config'
import type { CurriculumBasketType } from '../constants/curriculum-basket-type'
import type { CurriculumForm } from '../hooks/useCurriculumForm'
import { getCurriculumTimeUnitOptions } from '../utils/get-curriculum-time-unit-options'

type BasketCourseArraySectionProps = {
  basketIndex: number
}

const isDeleteCourseVisible = (
  basketType: CurriculumBasketType
): basketType is 'regular' | 'focus' =>
  ['regular', 'focus'].includes(basketType)

const isDeleteCourseDisabled = (
  basketType: CurriculumBasketType,
  coursesLength: number
) => {
  const { minItems } = BASKET_CONFIG_BY_TYPE[basketType]

  return coursesLength <= minItems
}

const isAddCourseVisible = (basketType: CurriculumBasketType) =>
  ['regular', 'focus'].includes(basketType)

const BasketCourseArraySection = (props: BasketCourseArraySectionProps) => {
  const { t } = useTranslation('curriculumsManagement')

  const form = useFormContext<CurriculumForm>()
  const { fields, append, remove } = useFieldArray<CurriculumForm>({
    control: form.control,
    name: `baskets.${props.basketIndex}.courses`
  })

  const timeUnitOptions = getCurriculumTimeUnitOptions(t)
  const basketType = form.watch(`baskets.${props.basketIndex}.type`)

  const createEmptyCourse = () => append(EMPTY_COURSE)
  const removeCourseFactory = (index: number) => () => remove(index)

  // Reset the semesters when the time unit changes
  const createTimeUnitChangeHandler =
    <FieldValue,>(
      courseIndex: number,
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onChange'] =>
    value => {
      inputProps.onChange?.(value)

      form.setValue(
        `baskets.${props.basketIndex}.courses.${courseIndex}.semesters`,
        EMPTY_SEMESTERS
      )
    }
  return (
    <div>
      <ul className={styles.list}>
        {fields.map((field, index) => (
          <li key={field.id} className={styles.listItem}>
            <div className={styles.col}>
              <FormField
                id={`baskets.${props.basketIndex}.courses.${index}.course`}
                name={`baskets.${props.basketIndex}.courses.${index}.course`}
                control={form.control}
                required
                label={t('label.course')}
                render={({ inputProps }) => (
                  <BasketCourseSelect {...inputProps} />
                )}
              />
              <FormField
                id={`baskets.${props.basketIndex}.courses.${index}.timeUnit`}
                name={`baskets.${props.basketIndex}.courses.${index}.timeUnit`}
                control={form.control}
                required
                label={t('label.time-unit')}
                render={({ inputProps }) => (
                  <Select
                    {...inputProps}
                    onChange={createTimeUnitChangeHandler(index, inputProps)}
                    options={timeUnitOptions}
                  />
                )}
              />
            </div>
            <div>
              <BasketCourseSemesterArraySection
                basketIndex={props.basketIndex}
                courseIndex={index}
              />
            </div>
            {isDeleteCourseVisible(basketType) ? (
              isDeleteCourseDisabled(basketType, fields.length) ? (
                <Tooltip
                  text={t(`tooltip.remove-basket-${basketType}`)}
                  renderTriggerAsChild
                  trigger={
                    <ButtonIcon
                      className={styles.deleteButton}
                      size="small"
                      variant="tertiary"
                      disabled
                      onClick={removeCourseFactory(index)}
                      ariaLabel={t('button.remove-course')}
                    >
                      <DeleteIcon />
                    </ButtonIcon>
                  }
                />
              ) : (
                <ButtonIcon
                  className={styles.deleteButton}
                  size="small"
                  variant="tertiary"
                  onClick={removeCourseFactory(index)}
                  ariaLabel={t('button.remove-course')}
                >
                  <DeleteIcon />
                </ButtonIcon>
              )
            ) : null}
          </li>
        ))}
      </ul>

      {isAddCourseVisible(basketType) ? (
        <div className={styles.buttonContainer}>
          <Button
            variant="secondary"
            onClick={createEmptyCourse}
            icon={<AddIcon />}
            className={styles.button}
          >
            {t('button.add-course')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default BasketCourseArraySection
